import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiFolder, FiMoreVertical, FiEdit2, FiTrash2, FiUsers, FiClock, FiSearch, FiGrid, FiList, FiCopy, FiArchive, FiDownload, FiUpload, FiTag, FiArrowUp, FiArrowDown, FiBarChart2, FiCheck, FiX } from 'react-icons/fi';
import DashboardLayout from '../Dashboard/DashboardLayout';
import ProjectsDashboard from './ProjectsDashboard';
import ConfirmModal from '../common/ConfirmModal';
import * as projectsApi from '../../api/projects';
import * as architecturesApi from '../../api/architectures';
import JSZip from 'jszip';
import { encryptArchitecturesBlobs, decryptArchitecturesBlobs } from '../../utils/encryption';
import { useToast } from '../../context/ToastContext';
import { useOrganizationStatus } from '../../hooks/useOrganizationStatus';
import QuotaExceededModal from '../common/QuotaExceededModal';
import ReadOnlyModal from '../common/ReadOnlyModal';
import { getPlanLimits } from '../../Config/plans';

interface Project {
  id: number;
  name: string;
  description: string;
  provider: string;
  architectureCount: number;
  lastModified: string;
  createdBy: string;
  color: string;
  tags?: string[];
  archived?: boolean;
}

const BLOCKED_STATUSES = ['TRIAL_EXPIRED', 'SUSPENDED', 'CANCELLED'];

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('projects');
  const toast = useToast();
  const { organization } = useOrganizationStatus();
  const isBlocked = BLOCKED_STATUSES.includes(organization?.status?.toUpperCase().replace(/-/g, '_') || '');
  const [projects, setProjects] = useState<Project[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>(t('filters.allProviders'));
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'architectures'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showArchived, setShowArchived] = useState(false);
  const [tagFilter, setTagFilter] = useState<string>(t('filters.allTags'));
  const [showDashboard, setShowDashboard] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<Set<number>>(new Set());
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ isOpen: boolean; projectId: number | null; projectName: string }>({
    isOpen: false,
    projectId: null,
    projectName: ''
  });

  // Load projects from API (base de données)
  useEffect(() => {
    loadProjects();
  }, []);

  // Reset filters when language changes
  useEffect(() => {
    setProviderFilter(t('filters.allProviders'));
    setTagFilter(t('filters.allTags'));
  }, [i18n.language, t]);

  // Selection handlers
  const toggleProjectSelection = (projectId: number) => {
    const newSelection = new Set(selectedProjects);
    if (newSelection.has(projectId)) {
      newSelection.delete(projectId);
    } else {
      newSelection.add(projectId);
    }
    setSelectedProjects(newSelection);
  };

  const selectAllProjects = () => {
    setSelectedProjects(new Set(filteredProjects.map(p => p.id)));
  };

  const deselectAllProjects = () => {
    setSelectedProjects(new Set());
  };

  const loadProjects = async () => {
    try {
      console.log(t('console.loadingProjects'));
      const apiProjects = await projectsApi.getProjects();
      console.log(t('console.projectsLoaded'), apiProjects.length);

      // Convertir le format API vers le format de l'interface
      const mappedProjects: Project[] = apiProjects.map((p: any) => {
        const provider = (p.provider || 'azure').toLowerCase();
        const providerColors: Record<string, string> = {
          'azure': '#0078D4',
          'aws': '#FF9900',
          'gcp': '#34A853',
          'fabric': '#038387',
        };

        return {
          id: p.id,
          name: p.name,
          description: p.description || '',
          provider: p.provider || 'Azure',
          architectureCount: Number(p.architectures_count) || 0,
          lastModified: p.updated_at || p.created_at,
          createdBy: p.created_by_name || 'User',
          color: providerColors[provider] || '#0078D4',
          tags: p.tags || []
        };
      });

      console.log(t('console.mappedProjects'), mappedProjects);
      setProjects(mappedProjects);
    } catch (error: any) {
      console.error(t('console.loadError'), error);

      // Fallback: charger depuis localStorage si l'API échoue
      const savedProjects = localStorage.getItem('projects.list');
      if (savedProjects) {
        console.warn(t('console.fallbackLocalStorage'));
        setProjects(JSON.parse(savedProjects));
      } else {
        console.warn(t('console.noProjects'));
        setProjects([]);
      }
    }
  };

  const handleCreateProject = async (projectData: Partial<Project>) => {
    try {
      console.log(t('console.creatingProject'), projectData);

      const createdProject = await projectsApi.createProject({
        name: projectData.name || 'Nouveau Projet',
        description: projectData.description || '',
        provider: projectData.provider || 'Azure' // Requis par le backend
      });

      console.log(t('console.projectCreated'), createdProject);

      // Recharger la liste des projets
      await loadProjects();

      setShowCreateModal(false);
      toast.success(t('messages.createSuccess'));

    } catch (error: any) {
      console.error(t('console.createError'), error);

      // Si c'est une erreur de quota (403 Quota exceeded), le modal s'affichera automatiquement
      // via useOrganizationStatus et le composant CreateProjectModal
      if (error.response?.status === 403 && error.response?.data?.error === 'Quota exceeded') {
        // Ne rien faire ici, le modal de quota se gère dans CreateProjectModal
        console.warn('⚠️  Quota exceeded:', error.response.data.message);
        // Optionnel: Toast pour info
        toast.error(error.response.data.message || 'Limite de projets atteinte');
      } else {
        // Autres erreurs
        const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
        toast.error(t('messages.createError', { error: errorMsg }));
      }
    }
  };

  const handleUpdateProject = async (projectData: Partial<Project>) => {
    if (!editingProject) return;

    try {
      console.log(t('console.updatingProject'), projectData);

      await projectsApi.updateProject(String(editingProject.id), {
        name: projectData.name,
        description: projectData.description
      });

      console.log(t('console.projectUpdated'));

      // Reload projects list
      await loadProjects();
      setEditingProject(null);
      toast.success(t('messages.updateSuccess'));

    } catch (error: any) {
      console.error(t('console.updateError'), error);
      const errorMsg = error.response?.data?.error || error.message;
      toast.error(t('messages.updateError', { error: errorMsg }));

      // Fallback to localStorage
      const updatedProjects = projects.map(p =>
        p.id === editingProject.id
          ? {
              ...p,
              name: projectData.name || p.name,
              description: projectData.description || p.description,
              provider: projectData.provider || p.provider,
              color: projectData.color || p.color,
              tags: projectData.tags || p.tags,
              lastModified: new Date().toISOString(),
            }
          : p
      );
      setProjects(updatedProjects);
      localStorage.setItem('projects.list', JSON.stringify(updatedProjects));
      setEditingProject(null);
    }
  };

  const handleDeleteProject = async (id: number) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;

    // Ouvrir la modale de confirmation
    setDeleteConfirmModal({
      isOpen: true,
      projectId: id,
      projectName: project.name
    });
  };

  const confirmDeleteProject = async () => {
    const id = deleteConfirmModal.projectId;
    if (!id) return;

    try {
      console.log(t('console.deletingProject'), id);

      await projectsApi.deleteProject(String(id));

      console.log(t('console.projectDeleted'));

      // Reload projects list
      await loadProjects();
      toast.success(t('messages.deleteSuccess'));

    } catch (error: any) {
      console.error(t('console.deleteError'), error);
      const errorMsg = error.response?.data?.error || error.message;
      toast.error(t('messages.deleteError', { error: errorMsg }));

      // Fallback to localStorage
      const updatedProjects = projects.filter(p => p.id === id);
      setProjects(updatedProjects);
      localStorage.setItem('projects.list', JSON.stringify(updatedProjects));
    }
  };

  const handleDuplicateProject = (project: Project) => {
    const duplicatedProject: Project = {
      ...project,
      id: Date.now(),
      name: `${project.name} (${t('messages.copy')})`,
      lastModified: new Date().toISOString(),
      architectureCount: project.architectureCount
    };
    const updatedProjects = [...projects, duplicatedProject];
    setProjects(updatedProjects);
    localStorage.setItem('projects.list', JSON.stringify(updatedProjects));
  };

  const handleArchiveProject = async (id: number) => {
    try {
      console.log(t('console.archivingProject'), id);

      await projectsApi.archiveProject(String(id));

      console.log(t('console.projectArchived'));

      // Reload projects list
      await loadProjects();
      toast.success(t('messages.archiveSuccess'));

    } catch (error: any) {
      console.error(t('console.archiveError'), error);
      const errorMsg = error.response?.data?.error || error.message;
      toast.error(t('messages.archiveError', { error: errorMsg }));

      // Fallback to localStorage
      const updatedProjects = projects.map(p =>
        p.id === id ? { ...p, archived: !p.archived } : p
      );
      setProjects(updatedProjects);
      localStorage.setItem('projects.list', JSON.stringify(updatedProjects));
    }
  };

  const handleExportProjects = async () => {
    if (selectedProjects.size === 0) {
      toast.warning(t('messages.exportSelectAtLeastOne'));
      return;
    }

    try {
      console.log(t('console.exportingProjects', { count: selectedProjects.size }));

      const zip = new JSZip();
      const selectedProjectsList = projects.filter(p => selectedProjects.has(p.id));

      for (const project of selectedProjectsList) {
        try {
          // Fetch project details from API
          const projectData = await projectsApi.getProject(String(project.id));

          // Fetch all architectures IDs for this project
          const architecturesList = await architecturesApi.getArchitecturesByProject(String(project.id));

          console.log(t('console.projectArchitectures', { name: project.name, count: architecturesList.length }));

          // Fetch complete details for each architecture (including resources, links, etc.)
          const completeArchitectures = [];
          for (const arch of architecturesList) {
            try {
              console.log(t('console.fetchingArchDetails', { name: arch.name }));
              const fullArchData = await architecturesApi.getArchitecture(String(arch.id));
              completeArchitectures.push(fullArchData);
            } catch (archError) {
              console.error(t('console.archDetailsError', { name: arch.name }), archError);
              // Utiliser les données partielles si l'API complète échoue
              completeArchitectures.push(arch);
            }
          }

          console.log(t('console.completeArchitectures', { name: project.name, count: completeArchitectures.length }));

          // Crypter les liens vers ADLS (file_path et file_url) pour la sécurité
          const encryptedArchitectures = encryptArchitecturesBlobs(completeArchitectures);

          // Create combined JSON with project + architectures (avec toutes les données: diagram_data, resources, links, etc.)
          const exportData = {
            type: 'geniac-project',
            version: '1.0',
            exportDate: new Date().toISOString(),
            project: projectData,
            architectures: encryptedArchitectures
          };

          // Add to zip with sanitized filename
          const filename = `${project.name.replace(/[^a-z0-9]/gi, '_')}_${project.id}.json`;
          zip.file(filename, JSON.stringify(exportData, null, 2));

        } catch (error) {
          console.error(t('console.exportProjectError', { name: project.name }), error);
          toast.error(t('messages.exportProjectError', { name: project.name }));
        }
      }

      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Download zip
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `geniac-projects-export-${new Date().toISOString().split('T')[0]}.zip`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success(t('messages.exportSuccess', { count: selectedProjectsList.length }));
      deselectAllProjects();

    } catch (error: any) {
      console.error(t('console.exportError'), error);
      toast.error(t('messages.exportError', { error: error.message }));
    }
  };

  const handleImportProjects = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.zip';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        let projectsToImport: any[] = [];

        // Handle ZIP files
        if (file.name.endsWith('.zip')) {
          const zip = new JSZip();
          const zipContent = await zip.loadAsync(file);

          // Process each JSON file in the zip
          for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
            if (filename.endsWith('.json') && !zipEntry.dir) {
              const text = await zipEntry.async('text');
              const data = JSON.parse(text);

              // Validate format
              if (data.type !== 'geniac-project') {
                toast.error(t('messages.importInvalidFile', { filename }));
                continue;
              }

              projectsToImport.push(data);
            }
          }
        }
        // Handle single JSON files
        else if (file.name.endsWith('.json')) {
          const text = await file.text();
          const data = JSON.parse(text);

          // Validate format
          if (data.type !== 'geniac-project') {
            toast.error(t('messages.importInvalidFormat'));
            return;
          }

          projectsToImport.push(data);
        } else {
          toast.error(t('messages.importUnsupportedFormat'));
          return;
        }

        if (projectsToImport.length === 0) {
          toast.error(t('messages.importNoValidProjects'));
          return;
        }

        // Import projects and architectures via API
        console.log(t('console.importingProjects', { count: projectsToImport.length }));
        let successCount = 0;
        let errorCount = 0;

        for (const projectData of projectsToImport) {
          try {
            // Create project
            const createdProject = await projectsApi.createProject({
              name: `${projectData.project.name} (${t('messages.copy')})`,
              description: projectData.project.description || '',
              provider: projectData.project.provider || 'Azure'
            });

            console.log(t('console.projectCreatedWithId', { name: createdProject.name, id: createdProject.id }));

            // Create architectures for this project
            if (projectData.architectures && Array.isArray(projectData.architectures)) {
              // Décrypter les liens vers ADLS avant l'import
              const decryptedArchitectures = decryptArchitecturesBlobs(projectData.architectures);

              for (const arch of decryptedArchitectures) {
                try {
                  await architecturesApi.createArchitecture({
                    projectId: String(createdProject.id),
                    name: arch.name,
                    description: arch.description || '',
                    provider: arch.provider,
                    diagramData: arch.diagram_data
                  });
                } catch (archError) {
                  console.error(t('console.importArchError', { name: arch.name }), archError);
                }
              }
              console.log(t('console.architecturesImported', { count: projectData.architectures.length }));
            }

            successCount++;
          } catch (error: any) {
            console.error(t('console.importProjectError'), error);
            errorCount++;
          }
        }

        // Reload projects
        await loadProjects();

        if (successCount > 0) {
          if (errorCount > 0) {
            toast.warning(t('messages.importSuccessWithErrors', { successCount, errorCount }));
          } else {
            toast.success(t('messages.importSuccess', { successCount }));
          }
        } else {
          toast.error(t('messages.importNoProjects'));
        }

      } catch (error: any) {
        console.error(t('console.importError'), error);
        toast.error(t('messages.importError', { error: error.message }));
      }
    };
    input.click();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) return t('dates.today');
    if (diffDays === 1) return t('dates.yesterday');
    if (diffDays < 7) return t('dates.daysAgo', { count: diffDays });
    return date.toLocaleDateString('fr-FR');
  };

  // Get all unique tags
  const allTags = Array.from(new Set(projects.flatMap(p => p.tags || [])));

  // Filter and sort projects
  const filteredProjects = projects
    .filter(project => {
      const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           project.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesProvider = providerFilter === t('filters.allProviders') || project.provider === providerFilter;
      const matchesArchived = showArchived ? project.archived : !project.archived;
      const matchesTag = tagFilter === t('filters.allTags') || (project.tags && project.tags.includes(tagFilter));
      return matchesSearch && matchesProvider && matchesArchived && matchesTag;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'date':
          comparison = new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
          break;
        case 'architectures':
          comparison = a.architectureCount - b.architectureCount;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  return (
    <>
      <DashboardLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('header.title')}</h1>
              <p className="text-gray-600 mt-2">{t('header.subtitle')}</p>
            </div>
            <div className="flex items-center gap-3">
              {selectedProjects.size > 0 && (
                <>
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-4 py-2 rounded-lg">
                    <FiCheck className="text-blue-600" size={18} />
                    <span className="text-sm font-medium text-blue-900">
                      {selectedProjects.size} {selectedProjects.size > 1 ? t('selection.selected_plural') : t('selection.selected')}
                    </span>
                    <button
                      onClick={deselectAllProjects}
                      className="ml-2 p-1 hover:bg-blue-100 rounded transition"
                      title={t('selection.deselectTitle')}
                    >
                      <FiX className="text-blue-600" size={16} />
                    </button>
                  </div>
                </>
              )}
              <button
                onClick={() => setShowDashboard(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition shadow-md"
              >
                <FiBarChart2 size={18} />
                <span className="font-medium">{t('buttons.dashboard')}</span>
              </button>
              <button
                onClick={handleImportProjects}
                className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition"
              >
                <FiUpload size={18} />
                <span className="font-medium">{t('buttons.import')}</span>
              </button>
              <button
                onClick={handleExportProjects}
                className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition"
                disabled={selectedProjects.size === 0}
                title={selectedProjects.size === 0 ? t('buttons.selectProjectsToExport') : t('buttons.exportTitle')}
              >
                <FiDownload size={18} />
                <span className="font-medium">{t('buttons.exportSelected')}{selectedProjects.size > 0 ? ` (${selectedProjects.size})` : ''}</span>
              </button>
              <button
                onClick={isBlocked ? undefined : () => setShowCreateModal(true)}
                disabled={isBlocked}
                title={isBlocked ? 'Votre période d\'essai est expirée. Choisissez un plan pour continuer.' : undefined}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg transition shadow-md ${isBlocked ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              >
                <FiPlus size={20} />
                <span className="font-medium">{t('header.newProject')}</span>
              </button>
            </div>
          </div>

          {/* Search, Filter and View Controls */}
          <div className="flex flex-wrap items-center gap-4 mb-4">
            {/* Search Bar */}
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder={t('search.placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                />
              </div>
            </div>

            {/* Provider Filter */}
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            >
              <option value={t('filters.allProviders')}>{t('filters.allProviders')}</option>
              <option value="Azure">Azure</option>
              <option value="AWS">AWS</option>
              <option value="GCP">GCP</option>
              <option value="Fabric">Microsoft Fabric</option>
            </select>

            {/* Tag Filter */}
            {allTags.length > 0 && (
              <select
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              >
                <option value={t('filters.allTags')}>{t('filters.allTags')}</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            )}

            {/* Sort Controls */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'date' | 'architectures')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            >
              <option value="date">{t('filters.sortByDate')}</option>
              <option value="name">{t('filters.sortByName')}</option>
              <option value="architectures">{t('filters.sortByArchitectures')}</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              title={sortOrder === 'asc' ? t('filters.ascending') : t('filters.descending')}
            >
              {sortOrder === 'asc' ? <FiArrowUp size={20} /> : <FiArrowDown size={20} />}
            </button>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'} transition`}
                title={t('views.grid')}
              >
                <FiGrid size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'} transition`}
                title={t('views.list')}
              >
                <FiList size={20} />
              </button>
            </div>
          </div>

          {/* Archive Toggle and Selection Controls */}
          <div className="flex items-center justify-between gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>{t('filters.showArchived')} ({projects.filter(p => p.archived).length})</span>
            </label>

            {filteredProjects.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={selectAllProjects}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium transition"
                >
                  {t('selection.selectAll')} ({filteredProjects.length})
                </button>
                {selectedProjects.size > 0 && (
                  <>
                    <span className="text-gray-400">|</span>
                    <button
                      onClick={deselectAllProjects}
                      className="text-sm text-gray-600 hover:text-gray-800 font-medium transition"
                    >
                      {t('selection.deselectAll')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Projects Display */}
        {projects.length === 0 ? (
          <div className="text-center py-20">
            <FiFolder size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('empty.noProjects')}</h3>
            <p className="text-gray-500 mb-6">{t('empty.noProjectsDescription')}</p>
            <button
              onClick={isBlocked ? undefined : () => setShowCreateModal(true)}
              disabled={isBlocked}
              title={isBlocked ? 'Votre période d\'essai est expirée. Choisissez un plan pour continuer.' : undefined}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg transition ${isBlocked ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            >
              <FiPlus size={20} />
              {t('empty.createProject')}
            </button>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-20">
            <FiSearch size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('empty.noResults')}</h3>
            <p className="text-gray-500 mb-6">{t('empty.noResultsDescription')}</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all border-2 ${
                  selectedProjects.has(project.id)
                    ? 'border-blue-500 ring-2 ring-blue-200'
                    : 'border-gray-200'
                }`}
              >
                {/* Color Bar */}
                <div className="h-2" style={{ backgroundColor: project.color }} />

                {/* Content */}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    {/* Checkbox */}
                    <div className="flex items-start gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={selectedProjects.has(project.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleProjectSelection(project.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <div
                        onClick={() => navigate(`/projects/${project.id}`)}
                        className="flex-1 cursor-pointer"
                      >
                        <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-blue-600 transition">
                          {project.name}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-2">
                          {project.description}
                        </p>
                      </div>
                    </div>

                    {/* Menu */}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenu(activeMenu === project.id ? null : project.id);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-full transition"
                      >
                        <FiMoreVertical className="text-gray-600" />
                      </button>

                      {activeMenu === project.id && (
                        <div
                          className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingProject(project);
                              setActiveMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                          >
                            <FiEdit2 size={16} />
                            {t('actions.edit')}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateProject(project);
                              setActiveMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                          >
                            <FiCopy size={16} />
                            {t('actions.duplicate')}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveProject(project.id);
                              setActiveMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                          >
                            <FiArchive size={16} />
                            {project.archived ? t('actions.unarchive') : t('actions.archive')}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(project.id);
                              setActiveMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-red-600"
                          >
                            <FiTrash2 size={16} />
                            {t('actions.delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-sm">
                      <FiFolder className="text-blue-600" />
                      <span className="text-gray-700 font-medium">{project.architectureCount}</span>
                      <span className="text-gray-500">{t('card.architectures')}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full font-medium">
                        {project.provider}
                      </span>
                      {project.archived && (
                        <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">
                          {t('card.archived')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Tags */}
                  {project.tags && project.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tags.map((tag, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">
                          <FiTag size={12} />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1">
                      <FiClock size={14} />
                      <span>{formatDate(project.lastModified)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FiUsers size={14} />
                      <span>{project.createdBy}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={filteredProjects.length > 0 && selectedProjects.size === filteredProjects.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            selectAllProjects();
                          } else {
                            deselectAllProjects();
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('table.project')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('table.provider')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('table.architectures')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('table.lastModified')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('table.createdBy')}
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredProjects.map((project) => (
                    <tr
                      key={project.id}
                      className={`hover:bg-gray-50 transition ${
                        selectedProjects.has(project.id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedProjects.has(project.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleProjectSelection(project.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4 cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                        <div className="flex items-center gap-3">
                          <div className="w-1 h-12 rounded-full" style={{ backgroundColor: project.color }} />
                          <div>
                            <div className="text-sm font-bold text-gray-900">{project.name}</div>
                            <div className="text-sm text-gray-500 line-clamp-1">{project.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {project.provider}
                        </span>
                      </td>
                      <td className="px-6 py-4 cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                        <div className="flex items-center gap-2 text-sm text-gray-900">
                          <FiFolder className="text-blue-600" />
                          <span className="font-medium">{project.architectureCount}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <FiClock size={14} />
                          <span>{formatDate(project.lastModified)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <FiUsers size={14} />
                          <span>{project.createdBy}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="relative inline-block">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMenu(activeMenu === project.id ? null : project.id);
                            }}
                            className="p-2 hover:bg-gray-200 rounded-full transition"
                          >
                            <FiMoreVertical className="text-gray-600" />
                          </button>

                          {activeMenu === project.id && (
                            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingProject(project);
                                  setActiveMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                              >
                                <FiEdit2 size={16} />
                                {t('actions.edit')}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDuplicateProject(project);
                                  setActiveMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                              >
                                <FiCopy size={16} />
                                {t('actions.duplicate')}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchiveProject(project.id);
                                  setActiveMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                              >
                                <FiArchive size={16} />
                                {project.archived ? t('actions.unarchive') : t('actions.archive')}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteProject(project.id);
                                  setActiveMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-red-600"
                              >
                                <FiTrash2 size={16} />
                                {t('actions.delete')}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create Project Modal */}
        {showCreateModal && (
          <CreateProjectModal
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreateProject}
          />
        )}
      </div>
    </DashboardLayout>

      {/* Create Project Modal */}
      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateProject}
        />
      )}

      {/* Edit Project Modal */}
      {editingProject && (
        <CreateProjectModal
          onClose={() => setEditingProject(null)}
          onCreate={handleUpdateProject}
          initialData={editingProject}
          isEditing={true}
        />
      )}

      {showDashboard && (
        <ProjectsDashboard
          projects={projects}
          onClose={() => setShowDashboard(false)}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={() => setDeleteConfirmModal({ isOpen: false, projectId: null, projectName: '' })}
        onConfirm={confirmDeleteProject}
        title={t('messages.deleteTitle')}
        message={t('messages.confirmDeleteProject', { name: deleteConfirmModal.projectName })}
        confirmText={t('actions.delete')}
        cancelText={t('modal.cancel')}
        variant="danger"
      />
    </>
  );
};

const CreateProjectModal: React.FC<{
  onClose: () => void;
  onCreate: (data: Partial<Project>) => void;
  initialData?: Partial<Project>;
  isEditing?: boolean;
}> = ({ onClose, onCreate, initialData, isEditing = false }) => {
  const { t } = useTranslation('projects');
  const { organization, subscription, usage } = useOrganizationStatus();
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showReadOnlyModal, setShowReadOnlyModal] = useState(false);
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    provider: initialData?.provider || 'Azure',
    color: initialData?.color || '#0078D4',
    tags: initialData?.tags || [] as string[]
  });
  const [tagInput, setTagInput] = useState('');

  const providerColors: Record<string, string> = {
    Azure:  '#0078D4',
    AWS:    '#FF9900',
    GCP:    '#EA4335',
    Fabric: '#038387',
  };

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !formData.tags.includes(trimmedTag)) {
      setFormData({ ...formData, tags: [...formData.tags, trimmedTag] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      // No need for toast here as the form has HTML5 validation with 'required' attribute
      return;
    }

    // Vérifier le statut de l'organisation (seulement pour création, pas pour édition)
    if (!isEditing && organization?.status) {
      const normalizedStatus = organization.status.toUpperCase().replace(/-/g, '_');

      if (normalizedStatus === 'OVER_QUOTA') {
        setShowQuotaModal(true);
        return;
      }

      if (['SUSPENDED', 'CANCELLED', 'TRIAL_EXPIRED'].includes(normalizedStatus)) {
        // Afficher le modal READ_ONLY
        setShowReadOnlyModal(true);
        return;
      }
    }

    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {isEditing ? t('modal.editTitle') : t('modal.createTitle')}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('modal.nameRequired')}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                placeholder={t('modal.namePlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('modal.descriptionLabel')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                rows={3}
                placeholder={t('modal.descriptionPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('modal.providerLabel')}
              </label>
              <select
                value={formData.provider}
                onChange={(e) => setFormData({
                  ...formData,
                  provider: e.target.value,
                  color: providerColors[e.target.value as keyof typeof providerColors]
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              >
                <option value="Azure">{t('modal.providerAzure')}</option>
                <option value="AWS">{t('modal.providerAWS')}</option>
                <option value="GCP">{t('modal.providerGCP')}</option>
                <option value="Fabric">Microsoft Fabric</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('modal.tagsLabel')}
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleTagKeyPress}
                  placeholder={t('modal.tagsPlaceholder')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <FiPlus size={20} />
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                    >
                      <FiTag size={14} />
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:text-red-600"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              {t('modal.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              {isEditing ? t('modal.save') : t('modal.create')}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de quota dépassé */}
      <QuotaExceededModal
        isOpen={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
        resourceType="project"
        currentUsage={usage?.projects || 0}
        quotaLimit={getPlanLimits(subscription?.plan).projects}
        plan={subscription?.plan || 'free'}
      />

      {/* Modal READ_ONLY */}
      <ReadOnlyModal
        isOpen={showReadOnlyModal}
        onClose={() => setShowReadOnlyModal(false)}
        resourceType="project"
      />
    </div>
  );
};

export default ProjectsPage;
